// ©2024 Austin App House. All rights reserved.
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import axios from 'axios';
import Select from '../../components/layout/CustomSelect';
import { toggleModal, toggleSecondModalClose } from '../../storage/actions/modalsActions';
import { dashboard as dashboardSelectStyle, dashboardSelectClassNames } from '../../utils/customSelectStyle';
import { getProfile } from '../../storage/actions/profile';
import {
  blockForCompany,
  blockForWorkOrder,
  setBlackListPerProject,
  setBlackListPerCompany,
} from '../../storage/actions/blockActions';
import Notification from '../notification';

const BlockModal = ({
  modalState,
  currentWorkOrder,
  getProfile,
  toggleModal: toggleModalAction,
  toggleSecondModalClose,
  profile,
  auth,
  blockForCompany: blockForCompanyAction,
  blockForWorkOrder: blockForWorkOrderAction,
  setBlackListPerProject,
  setBlackListPerCompany,
}) => {
  const [projects, setProjects] = useState([]);
  const [blockedProjects, setBlockedProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState([]);
  const [woCompanies, setWOCompanies] = useState([]);
  const [selectedWOCompany, setSelectedWOCompany] = useState([]);
  const [blockedWOCompanies, saveBlockedWOCompanies] = useState([]);
  const [isLoaded, setIsLoaded] = useState(false);

  const contractorId = modalState?.type?.contractorId;

  useEffect(() => {
    if (auth.isAuthenticated) getProfile(auth.user.id);
  }, [auth.isAuthenticated, auth.user.id, getProfile]);

  const closeModal = () => {
    toggleModalAction(false, 'blockModal');
  };

  const fetchProjects = async () => (
    axios.get('/api/projects')
      .then((res) => {
        if (res.data.success && res.data?.payload?.data?.length) {
          setProjects(res.data.payload.data.map(({ _id, name }) => ({
            value: _id,
            label: name,
          })));
        }
      })
  );

  const fetchBlockedProjects = (id) => axios.get(`/api/projects/blocked/${id}`)
    .then((res) => {
      if (res.data.success && res.data?.payload?.data?.length) {
        const newProjects = res.data.payload.data.map(({ _id, name }) => ({
          value: _id,
          label: name,
        }));
        setSelectedProject(newProjects);
        setBlockedProjects(newProjects);
      }
    });

  const fetchWOCompanies = () => axios.get('/api/wo-companies')
    .then((res) => {
      if (res.data.success && res.data?.payload?.data?.length) {
        setWOCompanies(res.data.payload.data.map(({ _id, name }) => ({
          value: _id,
          label: name,
        })));
      }
    });

  const fetchBlockedWOCompanies = (id) => axios.get(`/api/wo-companies/blocked/${id}`)
    .then((res) => {
      if (res.data.success && res.data.payload?.data?.length) {
        const newCompany = res.data.payload.data.map(({ _id, name }) => ({
          value: _id,
          label: name,
        }));
        saveBlockedWOCompanies(newCompany);
        setSelectedWOCompany(newCompany);
      }
    });

  useEffect(() => {
    if (!modalState?.isOpen || !contractorId) {
      setIsLoaded(false);
      return undefined;
    }

    let cancelled = false;

    const fetchData = async () => {
      try {
        setIsLoaded(false);
        toggleModalAction(true, 'loader', true);
        await Promise.all([
          fetchProjects(),
          fetchWOCompanies(),
          fetchBlockedProjects(contractorId),
          fetchBlockedWOCompanies(contractorId),
        ]);
        if (!cancelled) setIsLoaded(true);
      } catch (error) {
        Notification('error', { message: error.response?.data?.message || error.message });
        toggleModalAction(false, 'loader');
      } finally {
        toggleSecondModalClose('loader', true);
      }
    };

    fetchData();

    return () => {
      cancelled = true;
    };
  }, [modalState?.isOpen, contractorId]);

  const handleBlockForWOCompany = () => {
    const unblocked = blockedWOCompanies
      .filter((company) => !(selectedWOCompany.find((item) => item.value === company.value)));
    const blocked = selectedWOCompany
      .filter((company) => !(blockedWOCompanies.find((item) => item.value === company.value)));
    setBlackListPerCompany(
      blocked.map((company) => company.value),
      unblocked.map((company) => company.value),
      contractorId,
    );
  };

  const handleBlockForProjects = () => {
    const unblocked = blockedProjects
      .filter((project) => !(selectedProject.find((item) => item.value === project.value)));
    const blocked = selectedProject
      .filter((project) => !(blockedProjects.find((item) => item.value === project.value)));
    setBlackListPerProject(
      blocked.map((company) => company.value),
      unblocked.map((company) => company.value),
      contractorId,
    );
  };

  const isBlockedForClient = profile?.company?.blocked_users?.includes(contractorId);
  const isBlockedForWO = currentWorkOrder?.blocked_users?.includes(contractorId);

  if (!(modalState?.isOpen && isLoaded)) {
    return null;
  }

  return (
    <div
      className="modal_block dashboard-modal dashboard-modal--centered modal_import showed"
      role="presentation"
      onClick={closeModal}
    >
      <div
        className="modal_container"
        role="dialog"
        aria-modal="true"
        aria-labelledby="block-contractor-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal_head">
          <div className="modal_title" id="block-contractor-title">
            Block Contractor
          </div>
          <button
            type="button"
            className="close_btn"
            aria-label="close"
            onClick={closeModal}
          >
            ×
          </button>
        </div>
        <div className="modal_body">
          {currentWorkOrder && contractorId ? (
            <div className="field_col">
              <button
                type="button"
                className="dashboard-btn dashboard-btn--secondary dashboard-btn--full"
                onClick={() => blockForWorkOrderAction(currentWorkOrder._id, contractorId)}
              >
                {isBlockedForWO ? 'Unblock for WO' : 'Block for WO'}
              </button>
            </div>
          ) : null}

          <div className="field_col">
            <button
              type="button"
              className="dashboard-btn dashboard-btn--secondary dashboard-btn--full"
              onClick={() => blockForCompanyAction(contractorId)}
            >
              {isBlockedForClient ? 'Unblock for Client' : 'Block for Client'}
            </button>
          </div>

          <div className="field_col">
            <label className="field_name" htmlFor="woCompanySelect">
              Choose available company for block
            </label>
            <div className="dashboard-select-wrap">
              <Select
                inputId="woCompanySelect"
                isClearable
                isSearchable
                isMulti
                closeMenuOnSelect={false}
                options={woCompanies}
                value={selectedWOCompany}
                styles={dashboardSelectStyle}
                classNames={dashboardSelectClassNames}
                onChange={(value) => setSelectedWOCompany(value || [])}
              />
            </div>
          </div>

          <div className="field_col">
            <label className="field_name" htmlFor="projectSelect">
              Choose available project for block
            </label>
            <div className="dashboard-select-wrap">
              <Select
                inputId="projectSelect"
                isClearable
                isMulti
                closeMenuOnSelect={false}
                options={projects}
                value={selectedProject}
                styles={dashboardSelectStyle}
                classNames={dashboardSelectClassNames}
                onChange={(value) => setSelectedProject(value || [])}
              />
            </div>
          </div>
        </div>
        <div className="modal_footer">
          <button
            type="button"
            className="dashboard-btn dashboard-btn--primary"
            onClick={handleBlockForWOCompany}
          >
            Block for company
          </button>
          <button
            type="button"
            className="dashboard-btn dashboard-btn--primary"
            onClick={handleBlockForProjects}
          >
            Block for projects
          </button>
        </div>
      </div>
    </div>
  );
};

const mapDispatchToProps = {
  toggleModal,
  toggleSecondModalClose,
  getProfile,
  blockForCompany,
  blockForWorkOrder,
  setBlackListPerProject,
  setBlackListPerCompany,
};

const mapStateToProps = (state) => ({
  currentWorkOrder: state.workOrder.currentWorkOrder,
  modalState: state.modalState.blockModal,
  profile: state.profile,
  auth: state.auth,
});

export default connect(mapStateToProps, mapDispatchToProps)(BlockModal);
